/**
 * MODAL
 * Dialog
 */
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"
import { WithTranslation, withTranslation } from "react-i18next"
import Space from "./space"
import Button from "./button"
import Tooltip from "./tooltip"
import { STATUS_LOADED, STATUS_SAVING, STATUS_SEARCHING } from "@/redux/_status.types"
import SearchBar from "./search-bar"
import { useAnimationsDisabled } from "@/utils/animation-mode.utils"

interface OwnProps {
  backgroundColor?: string
  children?: any
  disableKeyEvents?: boolean
  disableClickOutside?: boolean
  disableOverflow?: boolean
  isCloseButtonVisible?: boolean
  isLarge?: boolean
  isFullScreen?: boolean
  onClose?: Function
  onDelete?: Function
  onSearch?: Function
  onNext?: Function
  onNextCta?: string
  status?: string
  title?: React.ReactNode
  nextDisabled?: boolean
  nextDisabledTooltip?: string
  nextDisabledTooltipPosition?: "top" | "bottom" | "left" | "right"
  className?: string
  contentClassName?: string
  isAnimated?: boolean //Animation d'ouverture et de fermeture (opt-in : la fermeture est alors retardée de l'animation)
  contentHeight?: number | string
  displayOwl?: boolean
  displayOwlTop?: number
  footer?: React.ReactNode
  footerLeft?: React.ReactNode //Left of the buttons of the footer
}

type Props = OwnProps & WithTranslation

function Modal(props: Props) {
  const { t } = props

  //Searching
  const [isSearching, setIsSearching] = useState(false)

  //Déclenché par close() ; le vrai démontage (props.onClose) n'arrive qu'une fois
  //l'animation de sortie terminée (cf onExitComplete plus bas) — permet une anim de
  //fermeture sans rien changer aux ~50 endroits qui font juste {isOpen && <Modal/>}
  const [isClosing, setIsClosing] = useState(false)
  const isAnimationsDisabled = useAnimationsDisabled()
  const isFading = Boolean(props.isAnimated) && !isAnimationsDisabled

  //Detect action
  //> Enter => go next
  //> Escape => close modal
  useEffect(() => {
    if (!props.disableKeyEvents) {
      const onKeyDown = (e) => {
        if (e.key === "Enter") {
          next()
        } else if (e.key === "Escape") {
          close()
        }
      }

      window.addEventListener("keydown", onKeyDown)
      return () => {
        window.removeEventListener("keydown", onKeyDown)
      }
    }
  })

  //Close dialog — avec l'animation, déclenche l'anim de sortie et le vrai onClose arrive via onExitComplete ;
  //sans animation, ferme tout de suite comme avant
  function close() {
    if (isFading) {
      setIsClosing(true)
    } else {
      handleExitComplete()
    }
  }

  //Appelé une fois l'anim de sortie terminée et l'élément réellement retiré du DOM
  function handleExitComplete() {
    if (props.onClose) {
      props.onClose()
    }
  }

  //Clic outside trigger close
  function clickOutside() {
    if (!props.disableClickOutside) {
      close()
    }
  }

  //Click on delete
  function deleteItem() {
    if (props.onDelete) {
      props.onDelete()
    }
  }

  //Custom style
  function getStyle() {
    const style: any = {
      backgroundColor: props.backgroundColor,
      minWidth: props.isFullScreen ? undefined : props.isLarge ? "780px" : undefined,
      maxWidth: props.isFullScreen ? undefined : props.isLarge ? "780px" : undefined
    }

    if (props.displayOwl) {
      style.backgroundImage = `url(${require("@/assets/owl.png")})`
      style.backgroundRepeat = "no-repeat"
      style.backgroundPosition = "-64px " + (props.displayOwlTop ? props.displayOwlTop : "8") + "px"
      style.backgroundSize = "260px auto"
      style.borderRadius = "12px"
      style.overflow = "hidden"
    }

    return style
  }

  //Function next
  function next() {
    if (props.onNext) {
      props.onNext()
    }
  }

  //Search
  function search(value) {
    setIsSearching(false)
    if (props.onSearch) props.onSearch(value)
  }

  const content = (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {!isClosing && (
        <motion.div
          key="modal-backdrop"
          className="modal-container flex"
          onClick={() => clickOutside()}
          initial={isFading ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          exit={isFading ? { opacity: 0 } : undefined}
          transition={{ duration: 0.18 }}>
          <motion.div
            key="modal-box"
            className={`${props.isFullScreen ? "modal-fullscreen" : "modal"} ${props.className || ""}`}
            onClick={(e) => {
              e.stopPropagation()
            }}
            initial={isFading ? { opacity: 0, scale: 0.96, y: 8 } : false}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={isFading ? { opacity: 0, scale: 0.96, y: 8 } : undefined}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={getStyle()}>
            <div className="flex" style={props.title ? { minHeight: 52 } : undefined}>
              {props.title && <h1>{props.title}</h1>}

              <Space />

              {props.onSearch && (
                <div style={{ width: "300px", marginLeft: "80px" }}>
                  <SearchBar
                    onSearch={search}
                    status={isSearching ? STATUS_SEARCHING : STATUS_LOADED}
                    onSearchStart={() => setIsSearching(true)}
                  />
                </div>
              )}
            </div>

            <div
              className={`${props.isFullScreen ? "modal-content-fullscreen" : "modal-content"} ${props.contentClassName || ""}`}
              style={{ overflowY: props.disableOverflow ? "initial" : "auto", maxHeight: props.contentHeight ?? undefined }}>
              {props.children}
            </div>

            <div className="flex">
              {props.onDelete && <Button onClick={deleteItem}>{t("utils_delete")}</Button>}

              {props.footerLeft}

              <Space />

              {props.isCloseButtonVisible && <Button onClick={close}>{t("utils_close")}</Button>}

              {props.onNext && (
                <Tooltip text={props.nextDisabledTooltip} position={props.nextDisabledTooltipPosition ?? "top"}>
                  <Button
                    className="primary"
                    isLoading={props.status === STATUS_SAVING}
                    onClick={next}
                    disabled={props.nextDisabled}>
                    {props.onNextCta ? props.onNextCta : t("utils_next")}
                  </Button>
                </Tooltip>
              )}

              {props.footer}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  return props.disableClickOutside ? createPortal(content, document.body) : content
}

export default withTranslation()(Modal)
